import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Trash2, Plus } from 'lucide-react';
import Layout from '../components/Layout';
import { api, formatCurrency } from '../lib/api';
import { SERVICE_OPTIONS, DEFAULT_PAYMENT_TERMS_DAYS } from '../lib/services';

const addDays = (isoDate, days) => {
  const d = new Date(isoDate);
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
};

const InvoiceForm = () => {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefillClientId = searchParams.get('client_id');
  
  const [companies, setCompanies] = useState([]);
  const [clients, setClients] = useState([]);
  const [serviceMode, setServiceMode] = useState('preset'); // 'preset' | 'custom'
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({
    invoice_no: '',
    date: today,
    due_date: addDays(today, DEFAULT_PAYMENT_TERMS_DAYS),
    company_id: '',
    client_id: '',
    service: '',
    period: '',
    items: [{ desc: '', qty: 1, rate: 0, amt: 0 }],
    gst_percent: 18,
    tds_percent: 2,
    notes: '',
    status: 'pending'
  });

  useEffect(() => {
    api.get('/companies').then(res => setCompanies(res.data));
    api.get('/clients').then(res => {
      setClients(res.data);
      // Prefill from ?client_id=... query string (used when coming from Client Detail)
      if (prefillClientId && !isEdit) {
        const c = res.data.find(x => x.id === prefillClientId);
        if (c) setForm(f => ({ ...f, client_id: c.id, company_id: c.company_id || f.company_id }));
      }
    });
    
    if (isEdit) {
      api.get(`/invoices/${id}`).then(res => {
        setForm({
          ...res.data,
          gst_percent: parseFloat(res.data.gst_percent),
          tds_percent: parseFloat(res.data.tds_percent)
        });
        if (res.data.service && !SERVICE_OPTIONS.includes(res.data.service)) {
          setServiceMode('custom');
        }
      });
    }
  }, [id, isEdit, prefillClientId]);

  useEffect(() => {
    if (form.company_id && !isEdit) {
      api.get(`/invoices/next-number/${form.company_id}`).then(res => {
        setForm(f => ({ ...f, invoice_no: res.data.invoice_no }));
      });
    }
  }, [form.company_id, isEdit]);

  const filteredClients = clients.filter(c => !form.company_id || c.company_id === form.company_id);

  const updateItem = (index, field, value) => {
    const newItems = [...form.items];
    newItems[index] = { ...newItems[index], [field]: field === 'desc' ? value : parseFloat(value) || 0 };
    if (field === 'qty' || field === 'rate') {
      newItems[index].amt = (newItems[index].qty || 0) * (newItems[index].rate || 0);
    }
    if (field === 'amt') {
      // Direct amount edit (edit mode): keep qty/rate consistent so backend totals don't drift
      const amt = parseFloat(value) || 0;
      newItems[index].amt = amt;
      newItems[index].qty = 1;
      newItems[index].rate = amt;
    }
    setForm({ ...form, items: newItems });
  };

  const addItem = () => setForm({ ...form, items: [...form.items, { desc: '', qty: 1, rate: 0, amt: 0 }] });
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  const subtotal = form.items.reduce((s, i) => s + (i.amt || 0), 0);
  const gst = subtotal * form.gst_percent / 100;
  const tds = subtotal * form.tds_percent / 100;
  const total = subtotal + gst - tds;

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        gst_percent: parseFloat(form.gst_percent),
        tds_percent: parseFloat(form.tds_percent)
      };
      if (isEdit) {
        await api.put(`/invoices/${id}`, payload);
        navigate(`/invoices/${id}`);
      } else {
        const res = await api.post('/invoices', payload);
        navigate(`/invoices/${res.data.id}`);
      }
    } catch (err) {
      alert('Error saving invoice: ' + (err.response?.data?.detail || err.message));
    }
  };

  const inputCls = "w-full bg-transparent border border-border focus:border-maroon outline-none px-3 py-2 font-body text-sm transition-colors";
  const labelCls = "block font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2";

  return (
    <Layout title={isEdit ? 'Edit Invoice' : 'New Invoice'}>
      <form onSubmit={handleSubmit} className="bg-surface border border-border p-8 max-w-5xl">
        <div className="grid grid-cols-2 gap-6 mb-8">
          <div>
            <label className={labelCls}>Company</label>
            <select data-testid="invoice-form-company" value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value, client_id: '' })} className={inputCls} required>
              <option value="">Select company</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Invoice No</label>
            <input data-testid="invoice-form-number" type="text" value={form.invoice_no} onChange={(e) => setForm({ ...form, invoice_no: e.target.value })} className={inputCls} required />
          </div>
          <div>
            <label className={labelCls}>Date</label>
            <input data-testid="invoice-form-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value, due_date: isEdit ? form.due_date : addDays(e.target.value, DEFAULT_PAYMENT_TERMS_DAYS) })} className={inputCls} required />
          </div>
          <div>
            <label className={labelCls}>Due Date</label>
            <input data-testid="invoice-form-due-date" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className={inputCls} required />
          </div>
          <div>
            <label className={labelCls}>Client</label>
            <select data-testid="invoice-form-client" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className={inputCls} required>
              <option value="">Select client</option>
              {filteredClients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Service</label>
            <select
              data-testid="invoice-form-service"
              value={serviceMode === 'custom' ? '__other__' : form.service}
              onChange={(e) => {
                if (e.target.value === '__other__') {
                  setServiceMode('custom');
                  setForm({ ...form, service: '' });
                } else {
                  setServiceMode('preset');
                  setForm({ ...form, service: e.target.value });
                }
              }}
              className={inputCls}
            >
              <option value="">Select service</option>
              {SERVICE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              <option value="__other__">Other…</option>
            </select>
            {serviceMode === 'custom' && (
              <input data-testid="invoice-form-service-custom" type="text" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} className={`${inputCls} mt-2`} placeholder="Custom service name" />
            )}
          </div>
          <div>
            <label className={labelCls}>Period</label>
            <input data-testid="invoice-form-period" type="text" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} placeholder="e.g. Jan 2025" className={inputCls} />
          </div>
        </div>

        <div className="mb-8">
          <h3 className="font-heading text-xl font-light text-text-primary mb-4">Line Items</h3>
          <table className="w-full mb-4">
            <thead>
              <tr>
                <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-2 text-left">Description</th>
                {!isEdit && <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-2 text-right w-20">Qty</th>}
                {!isEdit && <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-2 text-right w-32">Rate</th>}
                <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-2 text-right w-32">Amount</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {form.items.map((item, i) => (
                <tr key={i} className="border-b border-border">
                  <td className="py-2 pr-2"><input data-testid={`item-desc-${i}`} type="text" value={item.desc} onChange={(e) => updateItem(i, 'desc', e.target.value)} className={inputCls} /></td>
                  {!isEdit && <td className="py-2 px-2"><input data-testid={`item-qty-${i}`} type="number" value={item.qty} onChange={(e) => updateItem(i, 'qty', e.target.value)} className={`${inputCls} text-right`} /></td>}
                  {!isEdit && <td className="py-2 px-2"><input data-testid={`item-rate-${i}`} type="number" value={item.rate} onChange={(e) => updateItem(i, 'rate', e.target.value)} className={`${inputCls} text-right`} /></td>}
                  <td className="py-2 px-2">
                    {isEdit ? (
                      <input data-testid={`item-amt-${i}`} type="number" step="0.01" value={item.amt} onChange={(e) => updateItem(i, 'amt', e.target.value)} className={`${inputCls} text-right`} />
                    ) : (
                      <span className="font-numbers tabular-nums">{formatCurrency(item.amt)}</span>
                    )}
                  </td>
                  <td className="py-2">{form.items.length > 1 && <button type="button" data-testid={`remove-item-${i}`} onClick={() => removeItem(i)} className="text-text-secondary hover:text-maroon"><Trash2 className="w-4 h-4" strokeWidth={1} /></button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <button type="button" data-testid="add-item-button" onClick={addItem} className="font-body text-xs uppercase tracking-widest text-maroon hover:text-maroon-hover flex items-center">
            <Plus className="w-4 h-4 mr-1" strokeWidth={1} /> Add Item
          </button>
        </div>

        <div className="grid grid-cols-2 gap-6 mb-8">
          <div>
            <label className={labelCls}>GST %</label>
            <input data-testid="invoice-form-gst" type="number" value={form.gst_percent} onChange={(e) => setForm({ ...form, gst_percent: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>TDS %</label>
            <input data-testid="invoice-form-tds" type="number" value={form.tds_percent} onChange={(e) => setForm({ ...form, tds_percent: e.target.value })} className={inputCls} />
          </div>
        </div>

        <div className="mb-8">
          <label className={labelCls}>Notes</label>
          <textarea data-testid="invoice-form-notes" value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className={inputCls} />
        </div>

        <div className="border-t border-border pt-6 mb-8">
          <div className="ml-auto max-w-sm space-y-2">
            <div className="flex justify-between font-body text-sm"><span className="text-text-secondary">Subtotal</span><span className="font-numbers tabular-nums">{formatCurrency(subtotal)}</span></div>
            <div className="flex justify-between font-body text-sm"><span className="text-text-secondary">GST ({form.gst_percent}%)</span><span className="font-numbers tabular-nums">{formatCurrency(gst)}</span></div>
            <div className="flex justify-between font-body text-sm"><span className="text-text-secondary">TDS ({form.tds_percent}%)</span><span className="font-numbers tabular-nums">- {formatCurrency(tds)}</span></div>
            <div className="flex justify-between pt-2 border-t border-border"><span className="font-heading text-lg">Total</span><span className="font-numbers text-xl tabular-nums text-maroon">{formatCurrency(total)}</span></div>
          </div>
        </div>

        <div className="flex gap-4 justify-end">
          <button type="button" data-testid="cancel-button" onClick={() => navigate('/invoices')} className="bg-transparent text-text-secondary border border-border hover:border-maroon hover:text-maroon px-6 py-3 font-heading text-lg transition-colors">Cancel</button>
          <button type="submit" data-testid="save-invoice-button" className="bg-maroon text-white hover:bg-maroon-hover px-6 py-3 font-heading text-lg transition-colors">{isEdit ? 'Update' : 'Create'} Invoice</button>
        </div>
      </form>
    </Layout>
  );
};

export default InvoiceForm;
